import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency } from "@/lib/utils";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, Cell
} from "recharts";
import { Plane, MapPin, Users, TrendingUp, ChevronDown, ChevronUp } from "lucide-react";

const LOAD_COLORS = { high: "#10b981", mid: "#f59e0b", low: "#ef4444" };
const getLoadColor = (lf) => lf >= 80 ? LOAD_COLORS.high : lf >= 60 ? LOAD_COLORS.mid : LOAD_COLORS.low;

const CHART_COLORS = ["#0ea5e9", "#8b5cf6", "#f59e0b", "#10b981", "#ef4444", "#ec4899", "#6366f1", "#14b8a6"];

export default function RoutePerformance({ team }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRoute, setExpandedRoute] = useState(null);

  useEffect(() => {
    if (team?.id) {
      axios.get(`${API}/teams/${team.id}/route-performance`)
        .then(res => setData(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [team?.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400 gap-2">
        <div className="w-4 h-4 border-2 border-slate-300 border-t-sky-500 rounded-full animate-spin" />
        Loading route performance...
      </div>
    );
  }

  if (!data || data.routes.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-12 text-center text-slate-400">
          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No route performance data available yet.</p>
          <p className="text-xs mt-1">Route data will appear after the first quarter simulation.</p>
        </CardContent>
      </Card>
    );
  }

  // Build chart data for load factor comparison across quarters
  const chartData = [];
  if (data.routes.length > 0 && data.routes[0].history.length > 0) {
    const quarters = data.routes[0].history.map(h => h.quarter);
    for (const q of quarters) {
      const point = { quarter: `Q${q}` };
      for (const route of data.routes) {
        const h = route.history.find(h => h.quarter === q);
        point[route.route_id] = h ? h.load_factor : 0;
      }
      chartData.push(point);
    }
  }

  return (
    <div className="space-y-4" data-testid="route-performance-section">
      {/* Load Factor Trend Chart */}
      {chartData.length > 1 && (
        <Card className="border-slate-200">
          <CardHeader className="py-3 px-4 border-b border-slate-100">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-sky-500" />
              Load Factor by Route
            </CardTitle>
          </CardHeader>
          <CardContent className="py-4 px-2">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="quarter" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} tickFormatter={v => `${v}%`} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                {data.routes.map((route, i) => (
                  <Line
                    key={route.route_id}
                    type="monotone"
                    dataKey={route.route_id}
                    name={route.name || route.route_id}
                    stroke={CHART_COLORS[i % CHART_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Route Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.routes.map((route, i) => {
          const c = route.current;
          const isExpanded = expandedRoute === route.route_id;
          const lf = c.load_factor;

          return (
            <Card
              key={route.route_id}
              className="border-slate-200 overflow-hidden cursor-pointer hover:border-slate-300 transition-colors"
              data-testid={`route-card-${route.route_id}`}
              onClick={() => setExpandedRoute(isExpanded ? null : route.route_id)}
            >
              {/* Route Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-8 rounded-full"
                    style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                  />
                  <div>
                    <p className="font-semibold text-sm text-slate-800">
                      {route.name || route.route_id}
                    </p>
                    <p className="text-xs text-slate-400">
                      {route.from} → {route.to} · {route.distance_km.toLocaleString()} km
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {/* Load Factor Gauge */}
                  <div className="text-right">
                    <p className="text-lg font-bold font-mono" style={{ color: getLoadColor(lf) }}>
                      {lf}%
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">Load</p>
                  </div>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </div>

              {/* Summary Row */}
              <div className="grid grid-cols-3 gap-0 text-center py-2 px-4 text-xs">
                <div>
                  <p className="font-mono font-semibold text-slate-700">{c.passengers.toLocaleString()}</p>
                  <p className="text-slate-400">Passengers</p>
                </div>
                <div>
                  <p className="font-mono font-semibold text-emerald-600">{formatCurrency(c.revenue)}</p>
                  <p className="text-slate-400">Revenue</p>
                </div>
                <div>
                  <p className="font-mono font-semibold text-slate-700">{c.flights}</p>
                  <p className="text-slate-400">Flights</p>
                </div>
              </div>

              {/* Expanded Detail */}
              {isExpanded && (
                <div className="border-t border-slate-100 px-4 py-3 bg-slate-50 space-y-3">
                  {/* Capacity breakdown */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    {c.economy_passengers > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Economy Pax</span>
                        <span className="font-mono text-slate-700">{c.economy_passengers.toLocaleString()}</span>
                      </div>
                    )}
                    {c.business_passengers > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Business Pax</span>
                        <span className="font-mono text-slate-700">{c.business_passengers.toLocaleString()}</span>
                      </div>
                    )}
                    {c.economy_capacity > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Economy Capacity</span>
                        <span className="font-mono text-slate-700">{c.economy_capacity.toLocaleString()}</span>
                      </div>
                    )}
                    {c.business_capacity > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Business Capacity</span>
                        <span className="font-mono text-slate-700">{c.business_capacity.toLocaleString()}</span>
                      </div>
                    )}
                  </div>

                  {/* Quarter-by-quarter mini chart */}
                  {route.history.length > 1 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Revenue by Quarter</p>
                      <ResponsiveContainer width="100%" height={100}>
                        <BarChart data={route.history.map(h => ({ name: `Q${h.quarter}`, revenue: h.revenue, lf: h.load_factor }))}>
                          <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${(v/1e6).toFixed(1)}M`} />
                          <Tooltip formatter={(v, name) => name === "revenue" ? formatCurrency(v) : `${v}%`} />
                          <Bar dataKey="revenue" radius={[3, 3, 0, 0]}>
                            {route.history.map((h, j) => (
                              <Cell key={j} fill={CHART_COLORS[i % CHART_COLORS.length]} opacity={0.6 + 0.4 * (j / Math.max(route.history.length - 1, 1))} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
